<template>
  <div class="landing-page-library space-y-4">
    <p role="status" aria-live="polite" class="text-sm" :class="messageClass" data-test="library-status">
      {{ message ? message.text : '' }}
    </p>

    <div v-if="!sites.length" class="card p-6 space-y-3" data-test="first-site">
      <h2 class="text-lg font-semibold" style="color: var(--text-primary);">
        Create your first landing site
      </h2>
      <p class="text-sm" style="color: var(--text-secondary);">
        A site holds your landing pages and gets its own Made.ly web address. You can add your own domain later.
      </p>
      <form v-if="!readOnly" class="grid gap-3 sm:grid-cols-2" @submit.prevent="createSite">
        <label class="flex flex-col text-sm">
          <span class="font-medium" style="color: var(--text-secondary);">Site name</span>
          <input v-model="siteForm.name" class="form-input mt-1" type="text" maxlength="255" required data-field="site-name">
        </label>
        <label class="flex flex-col text-sm">
          <span class="font-medium" style="color: var(--text-secondary);">Web address</span>
          <span class="flex items-center gap-1 mt-1 min-w-0">
            <input
              v-model="siteForm.label"
              class="form-input grow min-w-0"
              type="text"
              maxlength="63"
              pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?"
              data-field="site-label"
              aria-describedby="site-label-hint">
            <span v-if="defaultZone" class="text-sm break-all" style="color: var(--text-secondary);">.{{ defaultZone }}</span>
          </span>
          <span id="site-label-hint" class="text-xs mt-1" style="color: var(--text-secondary);">Lowercase letters, numbers and hyphens.</span>
          <span v-for="entry in siteErrors" :key="entry" class="text-xs text-red-700">{{ entry }}</span>
        </label>
        <div class="sm:col-span-2">
          <button type="submit" class="btn btn-primary" data-action="create-site" :disabled="busy">
            {{ busy ? 'Creating…' : 'Create site' }}
          </button>
        </div>
      </form>
    </div>

    <template v-else>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <h2 class="text-lg font-semibold" style="color: var(--text-primary);">
          Your pages
        </h2>
        <button
          v-if="!readOnly && !picker.open"
          type="button"
          class="btn btn-primary"
          data-action="new-page"
          @click="openPicker">
          New page
        </button>
      </div>

      <form v-if="picker.open" class="card p-4 space-y-4" data-test="page-picker" @submit.prevent="createPage">
        <fieldset class="space-y-2">
          <legend class="text-sm font-medium" style="color: var(--text-secondary);">
            How do you want to start?
          </legend>
          <label class="flex items-start gap-2 text-sm">
            <input v-model="picker.mode" type="radio" value="manual" data-field="mode-manual">
            <span><strong>Start from a template</strong> and write the text yourself.</span>
          </label>
          <label class="flex items-start gap-2 text-sm">
            <input v-model="picker.mode" type="radio" value="ai" data-field="mode-ai">
            <span><strong>Write it for me.</strong> We draft the page from your business details and services; you review every word before publishing.</span>
          </label>
        </fieldset>

        <div v-if="templatesState === 'loading'" class="text-sm" style="color: var(--text-secondary);">
          Loading templates…
        </div>
        <div v-else-if="templatesState === 'error'" class="text-sm text-red-700" role="alert">
          Templates couldn't be loaded.
          <button type="button" class="underline" @click="loadTemplates">
            Try again
          </button>
        </div>
        <fieldset v-else class="space-y-2">
          <legend class="text-sm font-medium" style="color: var(--text-secondary);">
            Template
          </legend>
          <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label
              v-for="template in templates"
              :key="template.id"
              class="card p-3 flex flex-col gap-2 cursor-pointer border"
              :style="picker.templateId === template.id ? 'border-color: var(--color-primary, #2563eb); border-width: 2px;' : ''">
              <img
                v-if="template.thumbnail_url"
                :src="template.thumbnail_url"
                alt=""
                class="w-full h-28 object-cover rounded">
              <span class="flex items-center gap-2 text-sm">
                <input v-model="picker.templateId" type="radio" :value="template.id" :data-field="`template-${template.id}`">
                <span style="color: var(--text-primary);">{{ template.name }}</span>
              </span>
            </label>
          </div>
        </fieldset>

        <div class="grid gap-3 sm:grid-cols-2">
          <label v-if="sites.length > 1" class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Site</span>
            <select v-model="picker.siteId" class="form-select mt-1" data-field="page-site">
              <option v-for="site in sites" :key="site.id" :value="site.id">{{ site.name }}</option>
            </select>
          </label>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Page name</span>
            <input v-model="picker.title" class="form-input mt-1" type="text" maxlength="255" required data-field="page-name" @input="suggestPath">
          </label>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Address (path)</span>
            <input v-model="picker.path" class="form-input mt-1" type="text" required data-field="page-path" placeholder="/emergency-plumbing" @input="picker.pathEdited = true">
            <span v-for="entry in fieldErrors.path || []" :key="entry" class="text-xs text-red-700">{{ entry }}</span>
          </label>
        </div>

        <div v-if="picker.mode === 'ai'" class="space-y-2">
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Services to feature (one per line)</span>
            <textarea v-model="picker.services" class="form-input mt-1" rows="3" data-field="ai-services" placeholder="Leak repair&#10;Drain cleaning" />
          </label>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Tone (optional)</span>
            <input v-model="picker.tone" class="form-input mt-1" type="text" maxlength="200" data-field="ai-tone" placeholder="Friendly and plain-spoken">
          </label>
          <p class="text-xs" style="color: var(--text-secondary);">
            We only use facts you have confirmed: your business name, phone and service areas. We never invent reviews, prices or guarantees.
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <button type="submit" class="btn btn-primary" data-action="create-page" :disabled="busy || !canCreate">
            {{ busy ? progressLabel : 'Create page' }}
          </button>
          <button type="button" class="btn btn-secondary" :disabled="busy" @click="picker.open = false">
            Cancel
          </button>
        </div>
      </form>

      <div v-if="pagesState === 'loading' && !pages.length" class="text-sm" style="color: var(--text-secondary);">
        Loading pages…
      </div>
      <div v-else-if="pagesState === 'error'" class="rounded-lg border border-red-300 p-4 text-sm text-red-700" role="alert">
        Pages couldn't be loaded. Check your connection.
        <button type="button" class="underline" data-action="retry-pages" @click="loadPages">
          Try again
        </button>
      </div>
      <div v-else-if="!pages.length" class="card p-6 text-center" data-test="no-pages">
        <p class="font-medium" style="color: var(--text-primary);">
          No landing pages yet
        </p>
        <p class="text-sm mt-1" style="color: var(--text-secondary);">
          Create a page from a template, or let us draft one from your business details.
        </p>
      </div>

      <ul v-if="pages.length" class="grid gap-3 md:grid-cols-2 list-none p-0 m-0" data-test="page-list">
        <li v-for="page in pages" :key="page.id" class="card p-4 space-y-2" :data-page-id="page.id">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="font-medium break-words" style="color: var(--text-primary);">
                {{ page.title }}
              </p>
              <p class="text-sm break-all" style="color: var(--text-secondary);">
                {{ siteName(page.site_id) }} · {{ page.path }}
              </p>
            </div>
            <span class="text-xs rounded-full px-2 py-0.5 border shrink-0" :data-test="`status-${page.id}`" style="border-color: var(--border-primary, #e5e7eb); color: var(--text-primary);">
              {{ STATUS_LABELS[page.status] || page.status }}
            </span>
          </div>
          <p v-if="page.canonical_url" class="text-sm break-all">
            <a :href="page.canonical_url" target="_blank" rel="noopener" class="underline">{{ page.canonical_url }}</a>
          </p>
          <div class="flex flex-wrap gap-2">
            <button type="button" class="btn btn-secondary btn-sm" :data-action="`open-${page.id}`" @click="$emit('open', page.id)">
              {{ page.status === 'archived' || readOnly ? 'View' : 'Edit' }}
            </button>
            <button
              v-if="!readOnly && page.status !== 'archived'"
              type="button"
              class="btn btn-secondary btn-sm"
              :data-action="`duplicate-${page.id}`"
              :disabled="busy"
              @click="duplicate(page)">
              Duplicate
            </button>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { waitForOperation } from '~/javascript/services/landing_pages/api'

const props = defineProps({
  api: { type: Object, required: true },
  workspace: { type: Object, required: true },
  readOnly: { type: Boolean, default: false },
  pollIntervalMs: { type: Number, default: 2000 }
})

const emit = defineEmits(['open', 'changed'])

const STATUS_LABELS = { draft: 'Draft', published: 'Published', archived: 'Archived' }

const pages = ref([])
const pagesState = ref('loading')
const templates = ref([])
const templatesState = ref('idle')
const busy = ref(false)
const progressLabel = ref('Creating…')
const message = ref(null)
const fieldErrors = ref({})
const siteErrors = ref([])
const sites = computed(() => props.workspace.sites || [])
const defaultZone = computed(() => props.workspace.default_zone || '')
const siteForm = reactive({ name: props.workspace.name || '', label: slug(props.workspace.name || '').slice(0, 63) })
const picker = reactive({ open: false,
  mode: 'manual',
  templateId: '',
  siteId: '',
  title: '',
  path: '',
  pathEdited: false,
  services: '',
  tone: '' })
const canCreate = computed(() => picker.templateId && picker.title.trim() && picker.path.trim() &&
  (picker.mode === 'manual' || servicesList().length > 0))
const messageClass = computed(() => (message.value?.kind === 'success' ? 'text-green-700' : 'text-red-700'))

function slug(text) {
  return String(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

function siteName(siteId) {
  return sites.value.find((site) => site.id === siteId)?.name || 'Site'
}

function servicesList() {
  return picker.services.split('\n').map((line) => line.trim()).filter(Boolean).slice(0, 100)
}

function suggestPath() {
  if (!picker.pathEdited) picker.path = `/${slug(picker.title) || 'landing'}`
}

async function loadPages() {
  pagesState.value = 'loading'
  try {
    const result = await props.api.pages()
    pages.value = result.data
    pagesState.value = 'ready'
  } catch (error) {
    pagesState.value = 'error'
  }
}

async function loadTemplates() {
  templatesState.value = 'loading'
  try {
    const result = await props.api.templates()
    templates.value = result.data
    templatesState.value = 'ready'
    if (!picker.templateId && templates.value.length) picker.templateId = templates.value[0].id
  } catch (error) {
    templatesState.value = 'error'
  }
}

function openPicker() {
  picker.open = true
  picker.siteId = picker.siteId || sites.value[0]?.id || ''
  fieldErrors.value = {}
  if (templatesState.value !== 'ready') loadTemplates()
}

async function createSite() {
  busy.value = true
  siteErrors.value = []
  try {
    const body = { name: siteForm.name.trim() }
    if (siteForm.label) body.default_hostname_label = siteForm.label
    await props.api.createSite(body)
    message.value = { kind: 'success', text: 'Site created' }
    emit('changed')
  } catch (error) {
    siteErrors.value = Object.values(error.fields || {}).flat()
    message.value = { kind: 'error', text: 'The site could not be created.' }
  } finally {
    busy.value = false
  }
}

// Only confirmed workspace facts go into the grounded brief.
function brief() {
  const business = { name: props.workspace.name }
  const source = props.workspace.business || {}
  ;['phone', 'trade', 'service_areas', 'time_zone'].forEach((key) => {
    if (source[key]) business[key] = source[key]
  })
  const value = { business, services: servicesList().map((name) => ({ name })), brand_asset_ids: [], evidence: []}
  if (picker.tone.trim()) value.tone = picker.tone.trim()
  return value
}

async function createPage() {
  busy.value = true
  fieldErrors.value = {}
  message.value = null
  progressLabel.value = 'Creating…'
  let created = null
  try {
    const result = await props.api.createPage({ site_id: picker.siteId || sites.value[0].id,
      template_id: picker.templateId,
      path: picker.path.trim(),
      title: picker.title.trim() })
    created = result.data
  } catch (error) {
    busy.value = false
    fieldErrors.value = error.fields || {}
    message.value = { kind: 'error', text: createError(error) }
    return
  }
  if (picker.mode === 'ai') await generate(created)
  busy.value = false
  picker.open = false
  await loadPages()
  emit('open', created.id)
}

async function generate(page) {
  progressLabel.value = 'Writing your page…'
  try {
    const queued = await props.api.generate(page.id, { brief: brief(), template_id: page.template_id }, page.version)
    const operation = await waitForOperation(props.api, queued.data.id, { intervalMs: props.pollIntervalMs, attempts: 90 })
    if (operation?.state === 'succeeded') {
      message.value = { kind: 'success', text: 'Your draft is ready to review.' }
    } else {
      message.value = { kind: 'error', text: 'We couldn\'t write the page automatically. It starts with the template text instead.' }
    }
  } catch (error) {
    message.value = { kind: 'error', text: 'We couldn\'t write the page automatically. It starts with the template text instead.' }
  }
}

function createError(error) {
  if (error.code === 'path_in_use') return 'Another page on this site already uses that address.'
  if (error.code === 'field_invalid' && error.fields?.['business.phone']) {
    return 'Add your business phone number in Business settings before creating a page.'
  }
  if (error.code === 'field_invalid') return 'Check the highlighted fields.'
  if (error.code === 'outcome_unknown') return 'We couldn\'t confirm the page was created. Refresh the list before trying again.'
  return 'The page could not be created. Try again.'
}

async function duplicate(page) {
  busy.value = true
  message.value = null
  try {
    const source = await props.api.page(page.id)
    let number = 2
    const taken = pages.value.filter((row) => row.site_id === page.site_id).map((row) => row.path)
    while (taken.includes(`${page.path}-${number}`)) number += 1
    await props.api.duplicatePage(page.id, { title: `${page.title} (copy)`, path: `${page.path}-${number}` }, source.data.version)
    message.value = { kind: 'success', text: 'Page duplicated as a new draft' }
    await loadPages()
  } catch (error) {
    message.value = { kind: 'error', text: createError(error) }
  } finally {
    busy.value = false
  }
}

onMounted(() => {
  if (sites.value.length) loadPages()
  else pagesState.value = 'ready'
})
</script>
